import {
  type FigmaTemplateBenchmarkCase,
  type FigmaTemplateBenchmarkTier,
  type FigmaTemplateComponentKind,
  type FigmaTemplateNode,
  type FigmaTemplatePlatform,
} from "./figma-template-ontology-benchmark";

export const FIGMA_TEMPLATE_LIVE_INGEST_SCHEMA_VERSION =
  "cogo.figma-template-live-ingest.v1";

export interface FigmaTemplateSourceDescriptor {
  id?: string;
  title?: string;
  url?: string;
  fileKey?: string;
  nodeId?: string;
  tier: FigmaTemplateBenchmarkTier;
  platform: FigmaTemplatePlatform;
  intent: string;
  templateId?: string;
  designSystemId?: string;
  requiredPageIds?: readonly string[];
  requiredUiBlockIds?: readonly string[];
  requiredComponentIds?: readonly string[];
}

export interface FigmaTemplateSourceLocator {
  fileKey?: string;
  nodeId?: string;
}

export interface FigmaApiColor {
  r?: number;
  g?: number;
  b?: number;
  a?: number;
}

export interface FigmaApiPaint {
  type?: string;
  visible?: boolean;
  color?: FigmaApiColor;
}

export interface FigmaApiEffect {
  type?: string;
  visible?: boolean;
  radius?: number;
  offset?: {
    x?: number;
    y?: number;
  };
  color?: FigmaApiColor;
}

export interface FigmaApiTextStyle {
  fontFamily?: string;
  fontPostScriptName?: string;
  fontWeight?: number;
  fontSize?: number;
  lineHeightPx?: number;
  letterSpacing?: number;
}

export interface FigmaApiNode {
  id?: string;
  name?: string;
  type?: string;
  characters?: string;
  absoluteBoundingBox?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  };
  fills?: readonly FigmaApiPaint[];
  strokes?: readonly FigmaApiPaint[];
  cornerRadius?: number;
  effects?: readonly FigmaApiEffect[];
  style?: FigmaApiTextStyle;
  children?: readonly FigmaApiNode[];
}

export interface FigmaApiNodeEnvelope {
  document?: FigmaApiNode;
}

export interface FigmaApiNodesResponse {
  nodes?: Readonly<Record<string, FigmaApiNodeEnvelope>>;
}

export interface FigmaApiFileResponse {
  document?: FigmaApiNode;
}

export interface BuildFigmaTemplateBenchmarkCasesFromApiInput {
  source: FigmaTemplateSourceDescriptor;
  response: FigmaApiNodesResponse | FigmaApiFileResponse;
}

export interface FigmaTemplateFetchResponse {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}

export type FigmaTemplateFetchLike = (
  url: string,
  init?: {
    headers?: Record<string, string>;
  },
) => Promise<FigmaTemplateFetchResponse>;

export interface FetchFigmaTemplateBenchmarkCaseInput extends FigmaTemplateSourceDescriptor {
  accessToken?: string;
  apiBaseUrl?: string;
  fetcher?: FigmaTemplateFetchLike;
}

export interface FigmaTemplateLiveIngestProofFixture {
  source: FigmaTemplateSourceDescriptor;
  response: FigmaApiNodesResponse;
}

export function parseFigmaTemplateSourceUrl(
  url: string,
): FigmaTemplateSourceLocator {
  try {
    const parsed = new URL(url);
    const fileKey = parsed.pathname.match(
      /\/(?:file|design|proto|community\/file)\/([^/?#]+)/,
    )?.[1];
    const rawNodeId = parsed.searchParams.get("node-id") ?? undefined;
    return {
      fileKey: fileKey ? decodeURIComponent(fileKey) : undefined,
      nodeId: rawNodeId ? normalizeFigmaNodeId(rawNodeId) : undefined,
    };
  } catch {
    return {};
  }
}

export function buildFigmaTemplateBenchmarkCaseFromFigmaNode(
  source: FigmaTemplateSourceDescriptor,
  node: FigmaApiNode,
): FigmaTemplateBenchmarkCase {
  const locator = normalizeSourceLocator(source);
  const caseId = source.id ?? slugify(source.title ?? node.name ?? "figma");
  const templateId =
    source.templateId ??
    `figma-template:${locator.fileKey ?? caseId}:${locator.nodeId ?? "document"}`;
  const designSystemId =
    source.designSystemId ?? `figma-design-system:${locator.fileKey ?? caseId}`;
  return {
    id: caseId,
    title: source.title ?? node.name ?? "Figma template",
    tier: source.tier,
    platform: source.platform,
    intent: source.intent,
    templateId,
    designSystemId,
    root: figmaApiNodeToTemplateNode(node),
    requiredPageIds: source.requiredPageIds,
    requiredUiBlockIds: source.requiredUiBlockIds,
    requiredComponentIds: source.requiredComponentIds,
  };
}

export function buildFigmaTemplateBenchmarkCasesFromApiResponse(
  input: BuildFigmaTemplateBenchmarkCasesFromApiInput,
): FigmaTemplateBenchmarkCase[] {
  const locator = normalizeSourceLocator(input.source);
  const root = extractRootNode(input.response, locator.nodeId);
  if (!root) {
    throw new Error(
      `Figma template source did not contain node ${locator.nodeId ?? "document"}.`,
    );
  }
  return [buildFigmaTemplateBenchmarkCaseFromFigmaNode(input.source, root)];
}

export async function fetchFigmaTemplateBenchmarkCaseFromFigmaApi(
  input: FetchFigmaTemplateBenchmarkCaseInput,
): Promise<FigmaTemplateBenchmarkCase> {
  const locator = normalizeSourceLocator(input);
  if (!locator.fileKey) {
    throw new Error("Figma file key is required for live template ingest.");
  }
  const fetcher =
    input.fetcher ?? (globalThis as { fetch?: FigmaTemplateFetchLike }).fetch;
  if (!fetcher) {
    throw new Error("No fetch implementation available for Figma ingest.");
  }
  const apiBaseUrl = input.apiBaseUrl ?? "https://api.figma.com";
  const endpoint = locator.nodeId
    ? `${apiBaseUrl}/v1/files/${encodeURIComponent(locator.fileKey)}/nodes?ids=${encodeURIComponent(locator.nodeId)}`
    : `${apiBaseUrl}/v1/files/${encodeURIComponent(locator.fileKey)}`;
  const response = await fetcher(endpoint, {
    headers: {
      Accept: "application/json",
      ...(input.accessToken ? { "X-Figma-Token": input.accessToken } : {}),
    },
  });
  if (!response.ok) {
    throw new Error(
      `Figma template ingest failed (${response.status}) for ${locator.fileKey}:${locator.nodeId ?? "document"}.`,
    );
  }
  const payload = (await response.json()) as
    | FigmaApiNodesResponse
    | FigmaApiFileResponse;
  const [benchmarkCase] = buildFigmaTemplateBenchmarkCasesFromApiResponse({
    source: input,
    response: payload,
  });
  return benchmarkCase;
}

export function buildRepresentativeFigmaApiTemplateFixtures(): FigmaTemplateLiveIngestProofFixture[] {
  return [
    {
      source: {
        id: "live-mobile-onboarding-auth",
        title: "Live-shaped mobile onboarding auth",
        url: "https://www.figma.com/design/liveMobileAuth/Mobile-Auth?node-id=1-1",
        tier: "mobile-simple",
        platform: "mobile",
        intent:
          "Create a mobile onboarding app with welcome copy, email field, password field, and continue action.",
      },
      response: {
        nodes: {
          "1:1": {
            document: {
              id: "1:1",
              name: "Mobile Auth Flow",
              type: "FRAME",
              absoluteBoundingBox: { x: 0, y: 0, width: 390, height: 844 },
              fills: [solidPaint(1, 1, 1)],
              style: { fontFamily: "Inter", fontSize: 16, fontWeight: 500 },
              children: [
                {
                  id: "1:2",
                  name: "Welcome Card",
                  type: "FRAME",
                  absoluteBoundingBox: {
                    x: 24,
                    y: 96,
                    width: 342,
                    height: 420,
                  },
                  fills: [solidPaint(0.97, 0.98, 1)],
                  cornerRadius: 20,
                  children: [
                    {
                      id: "1:3",
                      name: "Welcome Headline",
                      type: "TEXT",
                      characters: "Start your account",
                      absoluteBoundingBox: {
                        x: 48,
                        y: 132,
                        width: 282,
                        height: 34,
                      },
                      style: {
                        fontFamily: "Inter",
                        fontSize: 28,
                        fontWeight: 700,
                      },
                    },
                    {
                      id: "1:7",
                      name: "Welcome Supporting Copy",
                      type: "TEXT",
                      characters: "Use your email to continue securely.",
                      absoluteBoundingBox: {
                        x: 48,
                        y: 178,
                        width: 282,
                        height: 48,
                      },
                      style: {
                        fontFamily: "Inter",
                        fontSize: 16,
                        fontWeight: 400,
                      },
                    },
                    {
                      id: "1:4",
                      name: "Email Input",
                      type: "INSTANCE",
                      absoluteBoundingBox: {
                        x: 48,
                        y: 250,
                        width: 282,
                        height: 48,
                      },
                      fills: [solidPaint(1, 1, 1)],
                    },
                    {
                      id: "1:5",
                      name: "Password Input",
                      type: "INSTANCE",
                      absoluteBoundingBox: {
                        x: 48,
                        y: 314,
                        width: 282,
                        height: 48,
                      },
                      fills: [solidPaint(1, 1, 1)],
                    },
                    {
                      id: "1:6",
                      name: "Continue Button",
                      type: "INSTANCE",
                      absoluteBoundingBox: {
                        x: 48,
                        y: 386,
                        width: 282,
                        height: 52,
                      },
                      fills: [solidPaint(0.15, 0.39, 0.92)],
                    },
                  ],
                },
              ],
            },
          },
        },
      },
    },
    {
      source: {
        id: "live-mobile-booking-list-detail",
        title: "Live-shaped mobile booking list detail",
        url: "https://www.figma.com/design/liveBooking/Booking-App?node-id=2-1",
        tier: "mobile-commerce",
        platform: "mobile",
        intent:
          "Create a mobile booking app with service cards, date field, checkout summary, and reserve action.",
      },
      response: {
        nodes: {
          "2:1": {
            document: {
              id: "2:1",
              name: "Booking Flow",
              type: "FRAME",
              absoluteBoundingBox: { x: 0, y: 0, width: 390, height: 844 },
              fills: [solidPaint(0.96, 0.98, 0.98)],
              style: { fontFamily: "Inter", fontSize: 16, fontWeight: 500 },
              children: [
                {
                  id: "2:2",
                  name: "Service List",
                  type: "FRAME",
                  absoluteBoundingBox: {
                    x: 20,
                    y: 96,
                    width: 350,
                    height: 308,
                  },
                  fills: [solidPaint(1, 1, 1)],
                  children: [
                    {
                      id: "2:8",
                      name: "Service Heading",
                      type: "TEXT",
                      characters: "Choose a service",
                      absoluteBoundingBox: {
                        x: 44,
                        y: 124,
                        width: 302,
                        height: 30,
                      },
                      style: {
                        fontFamily: "Inter",
                        fontSize: 24,
                        fontWeight: 700,
                      },
                    },
                    {
                      id: "2:3",
                      name: "Service Card",
                      type: "INSTANCE",
                      absoluteBoundingBox: {
                        x: 44,
                        y: 176,
                        width: 302,
                        height: 112,
                      },
                      fills: [solidPaint(0.94, 0.99, 0.98)],
                    },
                    {
                      id: "2:4",
                      name: "Book Button",
                      type: "INSTANCE",
                      absoluteBoundingBox: {
                        x: 44,
                        y: 316,
                        width: 302,
                        height: 52,
                      },
                      fills: [solidPaint(0.06, 0.46, 0.43)],
                    },
                  ],
                },
                {
                  id: "2:5",
                  name: "Checkout Summary",
                  type: "FRAME",
                  absoluteBoundingBox: {
                    x: 20,
                    y: 428,
                    width: 350,
                    height: 260,
                  },
                  fills: [solidPaint(1, 1, 1)],
                  children: [
                    {
                      id: "2:9",
                      name: "Checkout Heading",
                      type: "TEXT",
                      characters: "Confirm your slot",
                      absoluteBoundingBox: {
                        x: 44,
                        y: 460,
                        width: 302,
                        height: 30,
                      },
                      style: {
                        fontFamily: "Inter",
                        fontSize: 24,
                        fontWeight: 700,
                      },
                    },
                    {
                      id: "2:6",
                      name: "Date Input",
                      type: "INSTANCE",
                      absoluteBoundingBox: {
                        x: 44,
                        y: 512,
                        width: 302,
                        height: 48,
                      },
                      fills: [solidPaint(1, 1, 1)],
                    },
                    {
                      id: "2:7",
                      name: "Reserve Button",
                      type: "INSTANCE",
                      absoluteBoundingBox: {
                        x: 44,
                        y: 592,
                        width: 302,
                        height: 52,
                      },
                      fills: [solidPaint(0.06, 0.46, 0.43)],
                    },
                  ],
                },
              ],
            },
          },
        },
      },
    },
    {
      source: {
        id: "live-web-saas-landing",
        title: "Live-shaped web SaaS landing",
        url: "https://www.figma.com/design/liveSaas/SaaS-Landing?node-id=3-1",
        tier: "web-landing",
        platform: "web",
        intent:
          "Create a web SaaS landing page with navigation, hero section, feature cards, and signup action.",
      },
      response: {
        nodes: {
          "3:1": {
            document: {
              id: "3:1",
              name: "SaaS Landing Page",
              type: "FRAME",
              absoluteBoundingBox: { x: 0, y: 0, width: 1440, height: 900 },
              fills: [solidPaint(1, 1, 1)],
              style: { fontFamily: "Inter", fontSize: 18, fontWeight: 400 },
              children: [
                {
                  id: "3:2",
                  name: "Top Navigation",
                  type: "FRAME",
                  absoluteBoundingBox: {
                    x: 80,
                    y: 32,
                    width: 1280,
                    height: 64,
                  },
                  children: [
                    {
                      id: "3:9",
                      name: "Brand Label",
                      type: "TEXT",
                      characters: "Cogo Studio",
                      absoluteBoundingBox: {
                        x: 96,
                        y: 52,
                        width: 160,
                        height: 24,
                      },
                      style: {
                        fontFamily: "Inter",
                        fontSize: 18,
                        fontWeight: 700,
                      },
                    },
                    {
                      id: "3:3",
                      name: "Signup Button",
                      type: "INSTANCE",
                      absoluteBoundingBox: {
                        x: 1208,
                        y: 44,
                        width: 136,
                        height: 44,
                      },
                      fills: [solidPaint(0.15, 0.39, 0.92)],
                    },
                  ],
                },
                {
                  id: "3:4",
                  name: "Hero Section",
                  type: "FRAME",
                  absoluteBoundingBox: {
                    x: 80,
                    y: 160,
                    width: 1280,
                    height: 388,
                  },
                  children: [
                    {
                      id: "3:5",
                      name: "Hero Headline",
                      type: "TEXT",
                      characters: "Launch faster with Cogo",
                      absoluteBoundingBox: {
                        x: 120,
                        y: 196,
                        width: 720,
                        height: 68,
                      },
                      style: {
                        fontFamily: "Inter",
                        fontSize: 56,
                        fontWeight: 800,
                      },
                    },
                    {
                      id: "3:10",
                      name: "Hero Supporting Copy",
                      type: "TEXT",
                      characters:
                        "Turn a sourced design template into a governed app.",
                      absoluteBoundingBox: {
                        x: 120,
                        y: 292,
                        width: 620,
                        height: 64,
                      },
                      style: {
                        fontFamily: "Inter",
                        fontSize: 20,
                        fontWeight: 400,
                      },
                    },
                    {
                      id: "3:6",
                      name: "Demo Button",
                      type: "INSTANCE",
                      absoluteBoundingBox: {
                        x: 120,
                        y: 392,
                        width: 156,
                        height: 48,
                      },
                      fills: [solidPaint(0.15, 0.39, 0.92)],
                    },
                  ],
                },
                {
                  id: "3:7",
                  name: "Feature Cards",
                  type: "FRAME",
                  absoluteBoundingBox: {
                    x: 80,
                    y: 596,
                    width: 1280,
                    height: 188,
                  },
                  children: [
                    {
                      id: "3:8",
                      name: "Feature Card",
                      type: "INSTANCE",
                      absoluteBoundingBox: {
                        x: 120,
                        y: 620,
                        width: 360,
                        height: 132,
                      },
                      fills: [solidPaint(0.97, 0.98, 1)],
                    },
                  ],
                },
              ],
            },
          },
        },
      },
    },
  ];
}

function extractRootNode(
  response: FigmaApiNodesResponse | FigmaApiFileResponse,
  nodeId: string | undefined,
): FigmaApiNode | null {
  if ("nodes" in response && response.nodes) {
    const nodes = response.nodes;
    const exact = nodeId ? nodes[nodeId]?.document : undefined;
    if (exact) return exact;
    const first = Object.values(nodes)[0]?.document;
    return first ?? null;
  }
  if ("document" in response && response.document) return response.document;
  return null;
}

export function figmaApiNodeToTemplateNode(
  node: FigmaApiNode,
): FigmaTemplateNode {
  const name = node.name ?? node.type ?? "Figma node";
  return {
    id: node.id ?? slugify(name),
    name,
    type: node.type ?? "UNKNOWN",
    characters: node.characters,
    componentKind: inferTemplateComponentKind(node),
    layout: normalizeLayoutBox(node.absoluteBoundingBox),
    fills: extractPaintTokens(node),
    typography: extractTypographyTokens(node),
    actions: inferActionHints(node),
    dataInputs: inferDataInputHints(node),
    children: node.children?.map(figmaApiNodeToTemplateNode),
  };
}

function normalizeSourceLocator(
  source: Pick<FigmaTemplateSourceDescriptor, "fileKey" | "nodeId" | "url">,
): Required<FigmaTemplateSourceLocator> | FigmaTemplateSourceLocator {
  const parsed = source.url ? parseFigmaTemplateSourceUrl(source.url) : {};
  return {
    fileKey: source.fileKey ?? parsed.fileKey,
    nodeId: normalizeFigmaNodeId(source.nodeId ?? parsed.nodeId),
  };
}

function normalizeFigmaNodeId(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const decoded = decodeURIComponent(value);
  return decoded.includes(":") ? decoded : decoded.replace("-", ":");
}

function extractPaintTokens(node: FigmaApiNode): string[] {
  return unique(
    [...(node.fills ?? []), ...(node.strokes ?? [])]
      .map(paintTokenFromPaint)
      .filter((token): token is string => Boolean(token)),
  );
}

function paintTokenFromPaint(paint: FigmaApiPaint): string | null {
  if (paint.visible === false) return null;
  if (paint.type !== "SOLID" || !paint.color) return paint.type ?? null;
  const r = Math.round((paint.color.r ?? 0) * 255);
  const g = Math.round((paint.color.g ?? 0) * 255);
  const b = Math.round((paint.color.b ?? 0) * 255);
  const a = paint.color.a ?? 1;
  return a < 1
    ? `rgba(${r}, ${g}, ${b}, ${Number(a.toFixed(3))})`
    : rgbToHex(r, g, b);
}

function extractTypographyTokens(node: FigmaApiNode): string[] {
  const style = node.style;
  if (!style) return [];
  return [
    style.fontFamily ? `font:${style.fontFamily}` : null,
    typeof style.fontWeight === "number"
      ? `fontWeight:${style.fontWeight}`
      : null,
    typeof style.fontSize === "number" ? `fontSize:${style.fontSize}` : null,
    typeof style.lineHeightPx === "number"
      ? `lineHeight:${style.lineHeightPx}`
      : null,
    typeof style.letterSpacing === "number"
      ? `letterSpacing:${style.letterSpacing}`
      : null,
  ].filter((token): token is string => Boolean(token));
}

function normalizeLayoutBox(
  box: FigmaApiNode["absoluteBoundingBox"],
): FigmaTemplateNode["layout"] {
  if (!box) return undefined;
  return {
    x: finiteNumber(box.x),
    y: finiteNumber(box.y),
    width: finiteNumber(box.width),
    height: finiteNumber(box.height),
  };
}

function finiteNumber(value: number | undefined): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? Number(value.toFixed(3))
    : undefined;
}

function inferTemplateComponentKind(
  node: FigmaApiNode,
): FigmaTemplateComponentKind | undefined {
  const haystack = `${node.name ?? ""} ${node.type ?? ""}`.toLowerCase();
  if (/\b(button|cta|call to action|signup|reserve|book)\b/.test(haystack))
    return "button";
  if (
    /\b(input|textfield|text field|search|email|password|date)\b/.test(haystack)
  )
    return "input";
  if (/\b(card|tile)\b/.test(haystack)) return "card";
  if (/\b(tab|tabs)\b/.test(haystack)) return "tabs";
  if (/\b(table|grid|data grid|list)\b/.test(haystack)) return "table";
  if (/\b(nav|navbar|navigation|menu)\b/.test(haystack)) return "nav";
  if (/\b(modal|dialog)\b/.test(haystack)) return "modal";
  if (/\b(chart|graph|metric)\b/.test(haystack)) return "chart";
  if (/\b(image|photo|avatar)\b/.test(haystack)) return "image";
  if (node.type === "TEXT") return "text";
  return undefined;
}

function inferActionHints(node: FigmaApiNode): string[] {
  const name = node.name ?? "";
  const normalized = name.toLowerCase();
  const hints: string[] = [];
  if (/\bcontinue\b/.test(normalized)) hints.push("continue");
  if (/\b(primary\s+)?cta\b|\bprimary action\b/.test(normalized))
    hints.push("primaryAction");
  if (/\bsign\s*up\b|\bsignup\b/.test(normalized)) hints.push("openSignup");
  if (/\bdemo\b/.test(normalized)) hints.push("openDemo");
  if (/\bbook\b/.test(normalized)) hints.push("selectService");
  if (/\breserve\b/.test(normalized)) hints.push("reserveSlot");
  if (/\bcheckout\b/.test(normalized)) hints.push("openCheckout");
  return hints;
}

function inferDataInputHints(node: FigmaApiNode): string[] {
  const normalized =
    `${node.name ?? ""} ${node.characters ?? ""}`.toLowerCase();
  const hints: string[] = [];
  if (/\bemail\b/.test(normalized)) hints.push("email");
  if (/\bpassword\b/.test(normalized)) hints.push("password");
  if (/\bdate\b/.test(normalized)) hints.push("date");
  if (/\btime\b/.test(normalized)) hints.push("time");
  if (/\bservice\b/.test(normalized)) hints.push("service");
  if (/\bsearch\b/.test(normalized)) hints.push("searchQuery");
  return hints;
}

function solidPaint(r: number, g: number, b: number): FigmaApiPaint {
  return {
    type: "SOLID",
    color: { r, g, b, a: 1 },
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}

function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .trim() || "figma"
  );
}

function unique<T>(items: readonly T[]): T[] {
  return [...new Set(items)];
}
